<template>
  <div :class="cssClasses()">
    <div class="main-wrapper" data-test="start-view">
      <AgentDetails class="start-agent" />
      <div class="start-wrapper">
        <div class="row">
          <div class="col-xs-24">
            <h2 class="start-headline">
              {{ $t('start.head.h3') }}
            </h2>
            <p>{{ $t('start.head.p') }}</p>
            <ul class="info-list">
              <li>{{ $t('start.head.list.car') }}</li>
              <li>{{ $t('start.head.list.van') }}</li>
              <li>{{ $t('start.head.list.vanRegisteredAsCar') }}</li>
              <li>{{ $t('start.head.list.motorbike') }}</li>
            </ul>
          </div>
        </div>

        <div class="row">
          <div class="col-xs-24">
            <h3 class="headlines has-icon icon-user">
              {{ $t('start.form.personal.h3') }}
            </h3>

            <RadioSwitch
              id="customerType"
              v-model="form.customerType"
              name="customerType"
              :label1="$t('start.form.personal.customerTypePerson')"
              :label2="$t('start.form.personal.customerTypeCompany')"
              input-value1="person"
              input-value2="company"
              data-test="customerType"
              class="radio-switch--spacing"
            />
            <div
              v-if="isPerson && !isAgent"
              key="person"
            >
              <p>
                {{ $t('start.form.personal.age1') }}
                <FormInput
                  v-model="form.age"
                  v-validate.disable="`required|validate_ageOrYear_${minAge()}`"
                  :class="{'error-inline': errors.has('age')}"
                  :inline="true"
                  type="tel"
                  label="Alter"
                  :maxlength="2"
                  data-test="age"
                  data-vv-name="age"
                  @enter="submit"
                />
                {{ $t('start.form.personal.age2') }}
                <span class="zipcode-wrap">
                  {{ $t('start.form.personal.plz1') }}
                  <FormInput
                    v-model="form.zip"
                    v-validate.disable="'required|digits:4|validate_zipcode'"
                    :class="{'error-inline': errors.has('zip')}"
                    :inline="true"
                    type="tel"
                    label="PLZ"
                    :maxlength="4"
                    data-test="zip"
                    data-vv-name="zip"
                    @enter="submit"
                  />
                  .
                </span>
              </p>
              <p
                :class="{'js-error': errors.has('age')}"
                class="error-container"
              >
                {{ errors.first('age') }}
              </p>
              <p
                :class="{'js-error': errors.has('zip')}"
                class="error-container"
              >
                {{ errors.first('zip') }}
              </p>
            </div>
            <FormInput
              v-if="isPerson && isAgent"
              key="ageOrYear"
              v-model="form.ageOrYear"
              v-validate.disable="`required|validate_ageOrYear_${minAge()}`"
              :class="{'error-inline': errors.has('age')}"
              type="tel"
              :label="$t('start.form.personal.ageOrYear')"
              :maxlength="4"
              data-vv-name="age"
              data-test="age"
              :error="errors.first('age')"
              @enter="submit"
            />
            <FormInput
              v-if="isCompany || isAgent"
              key="zip"
              v-model="form.zip"
              v-validate.disable="'required|digits:4|validate_zipcode'"
              :class="{'error-inline': errors.has('zip')}"
              type="tel"
              :label="$t(isCompany ? 'start.form.personal.companyZip' : 'start.form.personal.zip')"
              :maxlength="4"
              data-vv-name="zip"
              data-test="zip"
              :error="errors.first('zip')"
              @enter="submit"
            />
            <div
              v-if="isPerson"
              key="nationality"
            >
              <div class="nationality-radios">
                <span class="nationality-label">
                  {{ $t('basics.nationality') }}
                </span>
                <div
                  :class="{'error': errors.has('nationalitySwiss')}"
                  class="radio-group"
                >
                  <FormRadio
                    v-model="form.nationalitySwiss"
                    v-validate.disable="'required'"
                    :input-value="true"
                    :label="$t('basics.swiss')"
                    type="radio"
                    name="nationalitySwiss"
                    data-test="nationalitySwiss"
                    class="radio-btn"
                  />

                  <FormRadio
                    v-model="form.nationalitySwiss"
                    :input-value="false"
                    :label="$t('basics.others')"
                    type="radio"
                    name="nationalityOthers"
                    data-test="nationalityOthers"
                    class="radio-btn"
                  />
                  <p
                    :class="{'js-error': errors.has('nationalitySwiss')}"
                    class="error-container space-bottom-xxs"
                  >
                    {{ errors.first('nationalitySwiss') }}
                  </p>
                </div>
              </div>

              <FormSelect
                v-if="showNationality"
                v-model="form.nationalityOthers"
                v-validate.disable="'required'"
                :options="nationalityOptions()"
                :error="errors.first('otherNationality')"
                :label="$t('start.form.personal.otherNationality')"
                data-test="nationalityOthersSelect"
                class="form-select--width"
                data-vv-name="otherNationality"
              />
            </div>

            <FormSelect
              v-if="isCompany"
              key="industry"
              v-model="industry"
              v-validate.disable="isCompany ? 'required' : ''"
              :options="industryOptions"
              :error="errors.first('industry')"
              :label="$t('start.form.personal.industry')"
              data-vv-name="industry"
              data-test="industry"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-xs-24">
            <h3 class="headlines has-icon icon-car">
              {{ $t('start.form.car.h3') }}
            </h3>

            <div class="clearfix form-select-wrap">
              <FormWrapper
                id="vehicleType"
                class="vehicleType"
                :label="$t('basics.vehicleType')"
              >
                <div class="radio-group w-100">
                  <FormRadio
                    v-model="form.vehicleRegistrationType"
                    input-value="01"
                    :label="$t('vehicleData.vehicleRegistrationType.carOrVan')"
                    type="radio"
                    name="vehicleRegistrationType"
                    data-test="carOrVan"
                    class="radio-btn"
                  />
                  <FormRadio
                    v-model="form.vehicleRegistrationType"
                    input-value="03"
                    :label="$t('vehicleData.vehicleRegistrationType.motorbike')"
                    type="radio"
                    name="vehicleRegistrationType"
                    data-test="motorbike"
                    class="radio-btn"
                  />
                </div>
              </FormWrapper>
            </div>

            <FormSelect
              v-model="form.initialRegistrationYear"
              v-validate.disable="'required'"
              :options="registerYearOptions()"
              :error="errors.first('initialRegistrationYear')"
              :label="$t('start.form.car.initialRegistrationYear')"
              data-test="initialRegistrationYear"
              data-vv-name="initialRegistrationYear"
            />

            <div v-if="isComfortSearchMode">
              <FormSelect
                v-if="!brandIsDisabled"
                v-model="form.brand"
                v-validate.disable="'required'"
                :options="brandOptions"
                :error="errors.first('brand')"
                :label="$t('start.form.car.brand')"
                data-test="brand"
                data-vv-name="brand"
              />

              <FormSelect
                v-if="!modelIsDisabled"
                v-model="form.model"
                v-validate.disable="'required'"
                :options="modelOptions"
                :error="errors.first('model')"
                :label="$t('start.form.car.model')"
                data-test="model"
                data-vv-name="model"
              />

              <FormSelect
                v-if="!fuelTypeIsDisabled"
                v-model="form.fuelTypeId"
                :options="fuelOptions"
                :label="$t('start.form.car.fuelType')"
                data-test="fuelType"
              />

              <FormSelect
                v-if="!powerIsDisabled"
                v-model="form.vehiclePowerRange"
                :options="powerOptions"
                :label="$t('start.form.car.powerRange')"
                data-test="powerRange"
              />
            </div>

            <div v-if="isTypenumberSearchMode">
              <FormInput
                v-model="form.typeCertificateNumber"
                v-validate.disable="'required|regex:[a-zA-Z0-9]{6}'"
                :error="errors.first('typeCertificateNumber')"
                :label="$t('start.form.car.typeCertificateNumber')"
                :placeholder="$t('start.form.car.typeCertificateNumber_placeholder')"
                type="text"
                :maxlength="maxLength"
                data-vv-name="typeCertificateNumber"
                data-test="typeCertificateNumber"
                @enter="submit"
              />
            </div>
          </div>
        </div>

        <div class="row start-of-insurance">
          <div class="col-xs-24">
            <h3 class="headlines has-icon icon-clock">
              {{ $t('start.form.startOfInsurance.h3') }}
            </h3>
            <FormInput
              v-model="form.startOfInsurance"
              v-validate.disable="`required|date_format:dd.MM.yyyy|after:${startOfInsuranceMin()}, true|before:${startOfInsuranceMax()}, true`"
              :error="errors.first('startOfInsurance')"
              :label="$t('start.form.startOfInsurance.startOfInsurance')"
              type="date"
              data-vv-name="startOfInsurance"
              data-test="startOfInsurance"
              :placeholder="$t('basics.datePlaceholder')"
              @enter="submit"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-xs-24">
            <Btn
              :text="$t('start.form.sendButton')"
              class="align-right space-bottom-xs"
              data-test="submitForm"
              @click="submit"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { addMonths, format, getYear } from 'date-fns'
import { range as _range } from 'lodash'
import FormValidator from '@mixins/form-validator'
import Form from '@mixins/form'
import AgentData from '@mixins/agentData'
import ZipcodeValidator from '@mixins/validators/ZipcodeValidator'
import AgeOrYearValidator from '@mixins/validators/AgeOrYearValidator'
import MixinCountries from '@mixins/countries'
import SafeDate from '@mixins/safeDate'
import ProductsMixin from '@mixins/products'
import Btn from '@cmp/Button'
import FormSelect from '@cmp/Forms/Select'
import FormInput from '@cmp/Forms/Input'
import RadioSwitch from '@cmp/Switch'
import FormRadio from '@cmp/Forms/Radio'
import FormWrapper from '@cmp/Forms/FormWrapper'
import brandsQuery from '@queries/vehicles/brands.gql'
import modelsQuery from '@queries/vehicles/models.gql'
import variantsQuery from '@queries/vehicles/variants.gql'
import AgentDetails from '@partials/AgentDetails'
import FlexDriveMixin from '@mixins/flexDrive'
import WhitelabelData from '@mixins/whitelabelData'

export default {
  name: 'InsuranceRequestStart',
  components: {
    Btn,
    FormSelect,
    RadioSwitch,
    FormInput,
    FormWrapper,
    FormRadio,
    AgentDetails,
  },
  mixins: [
    SafeDate,
    AgentData,
    FormValidator,
    Form,
    ZipcodeValidator,
    MixinCountries,
    AgeOrYearValidator,
    ProductsMixin,
    FlexDriveMixin,
    WhitelabelData,
  ],
  data() {
    return {
      form: {
        customerType: this.$store.state.newContract.customerType, // 'person'
        searchmode: this.$store.state.newContract.searchmode, // 'comfort',
        nationalitySwiss: this.$store.state.newContract.nationalitySwiss,
        nationalityOthers: this.$store.state.newContract.nationalityOthers,
        age: this.$store.state.newContract.personal.age,
        ageOrYear: this.$store.state.newContract.personal.age,
        nationality: this.$store.state.newContract.personal.nationality,
        nationalityName: this.$store.state.newContract.nationalityName,
        zip: this.$store.state.newContract.personal.zip,
        startOfInsurance: this.safeDate(this.$store.state.newContract.insurance.startOfInsurance),
        initialRegistrationYear: this.$store.state.newContract.vehicle.initialRegistrationYear,
        typeCertificateNumber: this.$store.state.newContract.vehicle.typeCertificateNumber,
        brand: this.$store.state.newContract.vehicle.brand,
        model: this.$store.state.newContract.vehicle.model,
        fuelTypeId: this.$store.state.newContract.vehicle.fuel.id,
        vehiclePowerRange: this.$store.state.newContract.vehicle.power.range,
        vehicleRegistrationType: this.$store.state.newContract.insurance.vehicleRegistrationType,
      },
      shouldScrollToFirstError: true,
      brands: [],
      models: [],
      variants: [],
      industry: null,
      maxLength: 6,
      vehicleRegistrationTypeOptions: [{
        value: '01',
        label: this.$t('vehicleData.vehicleRegistrationType.carOrVan'),
      }, {
        value: '03',
        label: this.$t('vehicleData.vehicleRegistrationType.motorbike'),
      }],
    }
  },
  computed: {
    showNationality() {
      return this.form.nationalitySwiss === false
    },
    isComfortSearchMode() {
      return this.form.searchmode === 'comfort'
    },
    isTypenumberSearchMode() {
      return this.form.searchmode === 'typenumber'
    },
    isCompany() {
      return this.form.customerType === 'company'
    },
    isPerson() {
      return this.form.customerType === 'person'
    },
    chooseOption() {
      return {
        value: null,
        label: this.$t('basics.chooseOption'),
        caption: true,
      }
    },
    noResultsOption() {
      return {
        value: null,
        label: this.$t('basics.noResultsOption'),
        caption: true,
      }
    },
    unknownOption() {
      return {
        value: '',
        label: this.$t('basics.unknownOption'),
      }
    },
    brandOptions() {
      const options = []
      options.push(this.chooseOption)

      this.brands.forEach((item) => {
        options.push({
          value: item.label,
          label: item.label,
        })
      })

      return options
    },
    modelOptions() {
      const options = []

      if (this.models.length) {
        const orderedModels = [...this.models].sort((a, b) => a.label.localeCompare(b.label))
        options.push(this.chooseOption)
        orderedModels.forEach((item) => {
          options.push({
            value: item.label,
            label: item.label,
          })
        })
      } else {
        options.push(this.noResultsOption)
      }

      return options
    },
    fuelOptions() {
      const options = []
      options.push(this.chooseOption)

      this.variants.forEach((singleVariant) => {
        options.push({
          value: singleVariant.fuel.id,
          label: singleVariant.fuel.label,
        })
      })

      options.push(this.unknownOption)

      return options
    },
    powerOptions() {
      const options = []
      options.push(this.chooseOption)

      const variant = this.variants.find(
        (singleVariant) => singleVariant.fuel.id === this.form.fuelTypeId,
      )

      if (variant) {
        variant.hpRanges.forEach((hpRange) => {
          let label = `${hpRange.from} - ${hpRange.to} PS`

          if (hpRange.from === hpRange.to) {
            label = `${hpRange.from} PS`
          }

          options.push({
            label,
            value: `${hpRange.from}:${hpRange.to}`,
          })
        })
      }

      options.push(this.unknownOption)

      return options
    },
    brandIsDisabled() {
      return !this.form.initialRegistrationYear
    },
    modelIsDisabled() {
      return !this.form.initialRegistrationYear
        || !this.form.brand
    },
    vehicleOptionIsDisabled() {
      return !this.form.initialRegistrationYear
        || !this.form.brand
        || !this.form.model
    },
    fuelTypeIsDisabled() {
      return this.vehicleOptionIsDisabled
    },
    powerIsDisabled() {
      return this.vehicleOptionIsDisabled || !this.form.fuelTypeId || this.form.fuelTypeId === '0'
    },
    industryOptions() {
      const options = []
      options.push(this.chooseOption)

      for (let i = 1; i < 14; i += 1) {
        options.push({
          value: `industry${i}`,
          label: this.$t(`start.form.personal.industries.industry${i}`),
        })
      }

      return options
    },
    isIndustryUnsupported() {
      return ['industry5', 'industry7', 'industry12'].indexOf(this.industry) >= 0
    },
    isAgent() {
      return this.$store.state.agent.isAgent === true
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    'form.nationalitySwiss': function (newValue) {
      if (newValue) {
        this.setNationalitySwiss()
      } else {
        this.form.nationality = ''
        this.form.nationalityName = ''
      }
    },
    // eslint-disable-next-line func-names
    'form.nationalityOthers': function (newValue) {
      if (newValue != null) {
        this.form.nationality = newValue
        this.countries.forEach((country) => {
          if (country.isoCode === newValue) {
            this.form.nationalityName = country.label
          }
        })
      }
    },
    // eslint-disable-next-line func-names
    'form.customerType': function (newValue) {
      if (newValue === 'company') {
        this.setNationalitySwiss()
        this.$store.commit('SET_GENDER', 'company')
      } else {
        this.form.nationality = null
        this.form.nationalitySwiss = null
        this.form.nationalityOthers = null
        this.$store.commit('SET_GENDER', null)
      }
    },
    // eslint-disable-next-line func-names
    'form.vehicleRegistrationType': function () {
      this.form.brand = null
    },
    // eslint-disable-next-line func-names
    'form.initialRegistrationYear': function () {
      this.form.brand = null
    },
    // eslint-disable-next-line func-names
    'form.brand': function () {
      this.form.model = null
    },
    // eslint-disable-next-line func-names
    'form.model': function () {
      this.form.fuelTypeId = null
    },
    // eslint-disable-next-line func-names
    'form.fuelTypeId': function () {
      this.form.vehiclePowerRange = null
    },
    'form.ageOrYear': function calculateAge(newValue) {
      if (this.isAgent && newValue > 80) {
        // Calculate age from birthyear
        this.form.age = getYear(new Date()) - newValue
      } else {
        this.form.age = newValue
      }
    },
    '$store.state.newContract.insurance.vehicleRegistrationType': function(newValue) {
      if (['01', '03'].indexOf(newValue) >= 0 && this.form.vehicleRegistrationType !== newValue) {
        this.form.vehicleRegistrationType = newValue
      }
      this.setBillingModel()
    },
    '$store.state.newContract.customerType': function(_) {
      this.setBillingModel()
    },
    '$route.path': function(_path) {
      this.checkVehicleRegistrationType()
    },
  },
  mounted() {
    this.checkAgentAuthorization().then((isAuthenticated) => {
      if (!isAuthenticated) {
        this.setAgentId(this.$route.query.agent)
      }
    })
    this.checkVehicleRegistrationType()
    const wlpKey = this.$route.query.wl
    this.getWlpData(wlpKey).then((result) => {
      this.$store.commit('SET_WHITELABELPARTNERKEY', result?.key)
      this.$store.commit('SET_WHITELABEL_DETAILS', result)
      this.setBillingModel()
    })
  },
  created() {
    // eslint-disable-next-line no-undef
    if (typeof sumcumo !== 'undefined' && typeof sumcumo.dex !== 'undefined') {
      // eslint-disable-next-line no-undef
      sumcumo.dex.fn.publish('show-contact-teaser')
    }
  },
  destroyed() {
    // eslint-disable-next-line no-undef
    if (typeof sumcumo !== 'undefined' && typeof sumcumo.dex !== 'undefined') {
      // eslint-disable-next-line no-undef
      sumcumo.dex.fn.publish('hide-contact-teaser')
    }
  },
  methods: {
    checkVehicleRegistrationType() {
      if (this.$route.path.includes(this.$t('routes.motorbikeBasePath'))) {
        this.form.vehicleRegistrationType = '03'
        this.$store.commit('SET_VEHICLEREGISTRATIONTYPE', '03')
      } else {
        this.form.vehicleRegistrationType = '01'
        this.$store.commit('SET_VEHICLEREGISTRATIONTYPE', '01')
      }
    },
    startOfInsuranceMin() {
      return format(new Date(), 'dd.MM.yyyy')
    },
    startOfInsuranceMax() {
      return format(addMonths(new Date(), 12), 'dd.MM.yyyy')
    },
    cssClasses() {
      return {
        error: this.error,
        [`${this.$prefixCss()}page`]: true,
      }
    },
    nationalityOptions() {
      return this.countries ? this.prepareNationalityOptions(this.countries, ['DE', 'AT', 'FR', 'IT', 'LI'], 'CH') : null
    },
    registerYearOptions() {
      const options = [{
        value: null,
        label: this.$t('basics.yearPlaceholder'),
        caption: true,
      }]
      const currentYear = getYear(new Date())
      const amount = 15

      _range(currentYear + 1, currentYear - amount - 1).forEach((year) => {
        options.push({
          label: year,
          value: year,
        })
      })

      return options
    },
    async submit() {
      if (this.isCompany && this.isIndustryUnsupported) {
        this.$router.push(this.localizedRoute('Declined'))
      } else {
        this.validate(async () => {
          this.queryProduct().then(() => {
            this.$store.commit('SET_DECLINEDREASON', null)
            this.nextStep()
          }).catch((_) => {
            this.$router.push(this.localizedRoute('Declined'))
          })
        })
      }
    },
    setNationalitySwiss() {
      this.form.nationality = 'CH'
      this.form.nationalitySwiss = true
      this.form.nationalityName = this.$t('basics.swiss')
      this.form.nationalityOthers = null
      this.$store.commit('SET_RESIDENCEPERMIT', null)
    },
    minAge() {
      const isMotorbike = this.$store.state.newContract.insurance.vehicleRegistrationType === '03'
      return isMotorbike ? 16 : 18
    },
    setBillingModel() {
      if (!this.flexDriveSupported) {
        this.$store.commit('SET_BILLINGMODEL', 'flat_rate')
      } else {
        this.$store.commit('SET_BILLINGMODEL', 'flex_drive')
      }
    },
  },
  apollo: {
    brands: {
      query: brandsQuery,
      fetchPolicy: 'cache-and-network',
      variables() {
        return {
          vehicleRegistrationType: this.form.vehicleRegistrationType,
          initialRegistrationYear: ~~this.form.initialRegistrationYear,
          // Force refetch on locale change
          locale: this.$i18n.locale,
        }
      },
      skip() {
        return !this.form.initialRegistrationYear
      },
      update(data) {
        if (data && data.vehicleBrands) {
          data.vehicleBrands.sort((brand1, brand2) => brand1.label.localeCompare(brand2.label))
          return data.vehicleBrands
        }
        return []
      },
    },
    models: {
      query: modelsQuery,
      fetchPolicy: 'cache-and-network',
      variables() {
        return {
          vehicleRegistrationType: this.form.vehicleRegistrationType,
          initialRegistrationYear: ~~this.form.initialRegistrationYear,
          brand: this.form.brand,
          // Force refetch on locale change
          locale: this.$i18n.locale,
        }
      },
      skip() {
        return !this.form.initialRegistrationYear
          || !this.form.brand
      },
      update(data) {
        return data.vehicleModels
      },
    },
    variants: {
      query: variantsQuery,
      fetchPolicy: 'cache-and-network',
      variables() {
        return {
          vehicleRegistrationType: this.form.vehicleRegistrationType,
          initialRegistrationYear: ~~this.form.initialRegistrationYear,
          brand: this.form.brand,
          model: this.form.model,
          // Force refetch on locale change
          locale: this.$i18n.locale,
        }
      },
      skip() {
        return !this.form.initialRegistrationYear
          || !this.form.brand
          || !this.form.model
      },
      update(data) {
        return data.vehicleFastQuoteVariants
      },
    },
  },
}
</script>

<style lang="scss">
@import "styles/index";
</style>
